"use client";

import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { ScrollArea } from "@/components/ui/Dialog/scroll-area";
import DynamicForm from "./DynamicForm";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { config } from "@/lib/config";

export default function FormSection({ data }) {
  const [isOpenJoinForm, setIsOpenJoinForm] = useState<any>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  //   contact us ref
  const contactContainerRef = useRef<HTMLDivElement | null>(null);
  const [isContactActive, setIsContactActive] = useState<boolean>(false);

  const isTaskforce = data?.field_title === "Join the Taskforce";
  const hasButton = !!data?.field_button_link?.title;
  const hasDescription = !!data?.field_description?.processed;

  // Outside click handler
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsActive(false);
      }
      if (
        contactContainerRef.current &&
        !contactContainerRef.current.contains(e.target as Node)
      ) {
        setIsContactActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      {data?.field_background_image ? (
        <div
          className="relative w-full min-h-[507px] py-[60px] mobileMax:py-[40px] betweenMobileTab:py-[50px] flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: `url('${config.apiBase}${data.field_image?.uri?.url}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/40 z-0" />

          {/* Centered Content */}
          <div className="relative z-10 max-w-[880px] mx-auto px-[20px] mobileMax:px-[6px] betweenMobileTab:px-[13px] text-center w-full">
            {data?.field_title && (
              <h3 className="text-white text-[42px] font-poppins mb-[20px] mobileMax:mb-[15px] font-semibold leading-normal mobileMax:text-[28px] bg-gradient-to-r from-[#48DBB2] to-[#A0DDFF] bg-clip-text text-transparent text-clip">
                {data?.field_title}
              </h3>
            )}

            {hasDescription && (
              <div
                dangerouslySetInnerHTML={{
                  __html: data?.field_description?.processed,
                }}
                className="text-white text-[16px] font-poppins mb-[40px] mobileMax:mb-[20px] leading-normal mobileMax:text-xsmall description"
              />
            )}

            <Dialog
              open={isOpenJoinForm}
              onOpenChange={(value) => setIsOpenJoinForm(value)}
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation mobileMax:flex mobileMax:justify-center"
              >
                {(hasButton ||
                  data?.field_add_form_field?.[0]?.field_label) && (
                  <DialogTrigger asChild>
                    <button
                      className={`px-[40px] py-[12px] font-poppins inline-flex items-center text-[#263AAD] text-[16px] font-semibold rounded-lg hover:!underline !no-underline mobileMax:text-small mobileMax:w-full mobileMax:justify-center bg-[linear-gradient(to_right,#48DBB2,#A0DDFF)]`}
                    >
                      {data?.field_button_link?.title || "Create"}
                    </button>
                  </DialogTrigger>
                )}
              </motion.div>
              <DialogContent
                hideOverlay={true}
                className="z-[99999] success-pledge-modal aboveLaptop:w-[75%] laptopMax:w-[95%] desktop:w-[70%] desktopMd:w-1/2 desktopLg:w-2/5 desktop:px-[91px] desktop:py-[50px] aboveMinMobile:px-[20px] aboveMinMobile:py-[30px] betweenMobileTab:px-[70px] betweenMobileTab:py-[38px] minMobile:px-[12px] minMobile:py-[27px] rounded-[30px] overflow-hidden"
              >
                <DialogHeader>
                  <DialogTitle className="bg-clip-text bg-[linear-gradient(to_right,#5DE29B,#263DA8)] text-clip text-[42px] font-semibold mobileMax:text-odd">
                    {data.field_form_title
                      ? data?.field_form_title
                      : isTaskforce
                        ? "Join the Taskforce"
                        : "Contact Us!"}
                  </DialogTitle>
                  <ScrollArea
                    hideScrollbar
                    className="max-h-[65vh] desktopMd:max-h-[70vh] m-0 px-3"
                  >
                    <DialogDescription>
                      {data?.field_add_form_field && (
                        <DynamicForm
                          formFields={data?.field_add_form_field}
                          afterFormSubmission={() => {
                            setOpenSuccessModal(true);
                            setIsOpenJoinForm(false);
                          }}
                          isTaskforce={isTaskforce}
                        />
                      )}
                    </DialogDescription>
                  </ScrollArea>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      ) : (
        <div className={`${isTaskforce ? "bg-white" : "bg-mapGray"}`}>
          <div
            className={`mini-container py-24 mobileMax:py-12 betweenMobileTab:py-12 relative betweenMobileTab:min-h-[480px] flex items-center justify-center lieExactTab:flex-col ${isTaskforce ? "" : "min-h-[700px] mobileMax:min-h-[400px]"}`}
          >
            <h3 className="exactLaptop:hidden text-[32px] font-poppins mb-[27px] mobileMax:mb-8 font-semibold leading-normal text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-[28px] lieExactTab:text-center mobileToDesk:mb-5">
              {data?.field_title}
            </h3>
            <Image
              src={`${config.apiBase}${data?.field_image?.uri?.url}`}
              alt={"Card image"}
              width={500}
              height={500}
              className={`${
                isTaskforce ? "w-[100%] h-[315px]" : "w-[57%] h-[515px]"
              } lieExactTab:w-[100%] lieTablets:max-h-full lieExactTab:h-full mobileMax:max-h-[500px] aspect-[650/515]`}
            />

            <div className="w-full max-w-[1200px] mx-auto lieExactTab:flex lieExactTab:justify-center lieExactTab:items-center lieExactTab:mt-10">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation flex items-center gap-5 desktop:gap-[200px] relative mobileMax:flex-col mobileMax:items-center mobileMax:justify-center"
              >
                <div
                  className={`${
                    isTaskforce ? "ml-16 lieExactTab:ml-0" : "w-[100%] ml-16 lieExactTab:ml-0"
                  } relative mobileMax:w-full mobileMax:px-4 lieExactTab:flex lieExactTab:flex-col lieExactTab:items-center lieExactTab:text-center`}
                >
                  <h3 className="text-[32px] lieExactTab:hidden font-poppins mb-[27px] mobileMax:mb-8 font-semibold leading-normal text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-[28px] lieExactTab:text-center mobileToDesk:mb-5">
                    {data?.field_title}
                  </h3>
                  <p
                    className="text-small lieExactTab:text-center betweenMobileTab:text-black text-[#003350] font-poppins font-medium mb-[13px] mobileMax:text-xsmall leading-normal [&>strong]:text-[#48DBB2]"
                    dangerouslySetInnerHTML={{
                      __html: data?.field_description?.processed,
                    }}
                  />

                  <Dialog
                    open={isOpenJoinForm}
                    onOpenChange={(value) => setIsOpenJoinForm(value)}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                      className="remove-animation-fluctuation lieExactTab:flex lieExactTab:justify-center"
                    >
                      {(hasButton ||
                        data?.field_add_form_field?.[0]?.field_label) && (
                        <DialogTrigger asChild>
                          <button
                            className={`${
                              isTaskforce ? "mt-5" : ""
                            } flex items-center justify-center max-w-[230px] px-8 font-poppins !text-white text-[16px] font-semibold min-h-[62px] rounded-lg hover:!underline !no-underline mobileMax:max-w-full mobileMax:text-xmedium mobileMax:mb-3 focus:!outline-none`}
                            style={{
                              background:
                                "linear-gradient(90deg, #316FA8 0%, #003350 100%)",
                            }}
                          >
                            {data?.field_button_link?.title || "Create"}
                          </button>
                        </DialogTrigger>
                      )}
                    </motion.div>
                    <DialogContent
                      hideOverlay={true}
                      className="z-[99999] success-pledge-modal aboveLaptop:w-[75%] laptopMax:w-[95%] desktop:w-[70%] desktopMd:w-1/2 desktopLg:w-2/5 desktop:px-[91px] desktop:py-[50px] aboveMinMobile:px-[20px] aboveMinMobile:py-[30px] betweenMobileTab:px-[70px] betweenMobileTab:py-[38px] minMobile:px-[12px] minMobile:py-[27px] rounded-[30px] overflow-hidden"
                    >
                      <DialogHeader>
                        <DialogTitle className="bg-clip-text bg-[linear-gradient(to_right,#316FA8,#003350)] text-clip text-[42px] font-semibold mobileMax:text-odd">
                          {data.field_form_title
                            ? data?.field_form_title
                            : isTaskforce
                              ? "Join the Taskforce"
                              : "Contact Us!"}
                        </DialogTitle>
                        <ScrollArea
                          hideScrollbar
                          className="max-h-[65vh] desktopMd:max-h-[70vh] m-0 px-3"
                        >
                          <DialogDescription>
                            {data?.field_add_form_field && (
                              <DynamicForm
                                formFields={data?.field_add_form_field}
                                afterFormSubmission={() => {
                                  setOpenSuccessModal(true);
                                  setIsOpenJoinForm(false);
                                }}
                                isTaskforce={isTaskforce}
                              />
                            )}
                          </DialogDescription>
                        </ScrollArea>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
